import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FiStar } from 'react-icons/fi';
import { LONG_DAILY, LONG_WEEKLY, LONG_MONTHLY, LONG_YEARLY } from '../data/horoscopeContent.fi';
import './Horoscope.css';

const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api';
const rawPrefix = process.env.REACT_APP_API_PREFIX;
const API_ENABLED = rawPrefix != null && String(rawPrefix).trim() !== '' && rawPrefix !== '/api';

const PERIODS = [
  { id: 'daily', label: 'Päivä', api: 'daily' },
  { id: 'weekly', label: 'Viikko', api: 'weekly' },
  { id: 'monthly', label: 'Kuukausi', api: 'monthly' },
  { id: 'yearly', label: 'Vuosi 2026', api: 'yearly' }
];

const SIGN_NAME_FI = {
  Aries: 'Oinas', Taurus: 'Härkä', Gemini: 'Kaksoset', Cancer: 'Rapu',
  Leo: 'Leijona', Virgo: 'Neitsyt', Libra: 'Vaaka', Scorpio: 'Skorpioni',
  Sagittarius: 'Jousimies', Capricorn: 'Kauris', Aquarius: 'Vesimies', Pisces: 'Kalat'
};

const zodiacSigns = [
  { name: 'Aries', nameFi: 'Oinas', symbol: '♈', dates: '21.3 – 19.4' },
  { name: 'Taurus', nameFi: 'Härkä', symbol: '♉', dates: '20.4 – 20.5' },
  { name: 'Gemini', nameFi: 'Kaksoset', symbol: '♊', dates: '21.5 – 20.6' },
  { name: 'Cancer', nameFi: 'Rapu', symbol: '♋', dates: '21.6 – 22.7' },
  { name: 'Leo', nameFi: 'Leijona', symbol: '♌', dates: '23.7 – 22.8' },
  { name: 'Virgo', nameFi: 'Neitsyt', symbol: '♍', dates: '23.8 – 22.9' },
  { name: 'Libra', nameFi: 'Vaaka', symbol: '♎', dates: '23.9 – 23.10' },
  { name: 'Scorpio', nameFi: 'Skorpioni', symbol: '♏', dates: '24.10 – 22.11' },
  { name: 'Sagittarius', nameFi: 'Jousimies', symbol: '♐', dates: '23.11 – 21.12' },
  { name: 'Capricorn', nameFi: 'Kauris', symbol: '♑', dates: '22.12 – 19.1' },
  { name: 'Aquarius', nameFi: 'Vesimies', symbol: '♒', dates: '20.1 – 19.2' },
  { name: 'Pisces', nameFi: 'Kalat', symbol: '♓', dates: '20.2 – 20.3' }
];

const ELEMENTS = {
  Aries: 'Fire', Taurus: 'Earth', Gemini: 'Air', Cancer: 'Water',
  Leo: 'Fire', Virgo: 'Earth', Libra: 'Air', Scorpio: 'Water',
  Sagittarius: 'Fire', Capricorn: 'Earth', Aquarius: 'Air', Pisces: 'Water'
};
const ELEMENT_FI = { Fire: 'Tuli', Earth: 'Maa', Air: 'Ilma', Water: 'Vesi' };
const PLANETS = {
  Aries: 'Mars', Taurus: 'Venus', Gemini: 'Mercury', Cancer: 'Moon',
  Leo: 'Sun', Virgo: 'Mercury', Libra: 'Venus', Scorpio: 'Pluto',
  Sagittarius: 'Jupiter', Capricorn: 'Saturn', Aquarius: 'Uranus', Pisces: 'Neptune'
};
const PLANET_FI = { Mars: 'Mars', Venus: 'Venus', Mercury: 'Merkurius', Moon: 'Kuu', Sun: 'Aurinko', Pluto: 'Pluto', Jupiter: 'Jupiter', Saturn: 'Saturnus', Uranus: 'Uranus', Neptune: 'Neptunus' };

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 h
const CACHE_KEY_PREFIX = 'horoscope_';
const memoryCache = {};

function getCached(signKey) {
  const key = CACHE_KEY_PREFIX + signKey;
  if (memoryCache[key]) {
    if (Date.now() - memoryCache[key].ts < CACHE_TTL_MS) return memoryCache[key].data;
    delete memoryCache[key];
  }
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts >= CACHE_TTL_MS) return null;
    memoryCache[key] = { data, ts };
    return data;
  } catch {
    return null;
  }
}

function setCached(signKey, data) {
  const key = CACHE_KEY_PREFIX + signKey;
  const entry = { data, ts: Date.now() };
  memoryCache[key] = entry;
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (_) {}
}

function getFallbackForPeriod(signName, periodId) {
  const el = ELEMENTS[signName] || '';
  const planet = PLANETS[signName] || '';
  const today = new Date().toISOString().split('T')[0];
  const week = Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
  const months = ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'];
  const month = months[new Date().getMonth()];
  const year = new Date().getFullYear();
  const longByPeriod = {
    daily: LONG_DAILY[signName],
    weekly: LONG_WEEKLY[signName],
    monthly: LONG_MONTHLY[signName],
    yearly: LONG_YEARLY[signName]
  };
  const horoscope = longByPeriod[periodId] || 'Sisältöä ei juuri nyt saatavilla.';
  const meta = { daily: { date: today }, weekly: { week }, monthly: { month, year }, yearly: { year: 2026 } };
  return { ...(meta[periodId] || {}), horoscope, element: el, rulingPlanet: planet };
}

function horoscopeTextWithFinnishSign(text, signNameEn) {
  if (!text || !signNameEn) return text;
  const nameFi = SIGN_NAME_FI[signNameEn];
  if (!nameFi) return text;
  return text.replace(new RegExp(signNameEn, 'gi'), nameFi);
}

function isApiErrorResult(result) {
  if (result.status !== 'rejected') return false;
  const r = result.reason;
  if (r?.code === 'ERR_CANCELED' || r?.name === 'CanceledError') return false;
  const status = r?.response?.status;
  const isNetworkError = r && !r.response;
  return status === 404 || status === 401 || status === 403 || isNetworkError;
}

const Horoscope = () => {
  const [selectedSign, setSelectedSign] = useState(null);
  const [byPeriod, setByPeriod] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiDisabled, setApiDisabled] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchAllForSign = useCallback(async (sign) => {
    if (!sign) return;
    const signKey = sign.name.toLowerCase();

    if (!API_ENABLED || apiDisabled) {
      const next = {};
      PERIODS.forEach((p) => { next[p.id] = getFallbackForPeriod(sign.name, p.id); });
      setByPeriod(next);
      setCached(signKey, next);
      return;
    }

    const cached = getCached(signKey);
    if (cached) {
      setByPeriod(cached);
      return;
    }
    setLoading(true);
    setByPeriod({});
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;
    const urls = PERIODS.map((p) =>
      p.api === 'yearly'
        ? `${API_PREFIX}/horoscope/yearly/${signKey}?year=2026`
        : `${API_PREFIX}/horoscope/${p.api}/${signKey}`
    );
    const settled = await Promise.allSettled(
      urls.map((url) => axios.get(url, { signal }))
    );
    if (signal.aborted) return;
    const anyApiError = settled.some(isApiErrorResult);
    if (anyApiError) setApiDisabled(true);
    const next = {};
    PERIODS.forEach((p, i) => {
      const result = settled[i];
      if (result.status === 'fulfilled' && result.value?.data?.horoscope) {
        next[p.id] = result.value.data;
      } else {
        next[p.id] = getFallbackForPeriod(sign.name, p.id);
      }
    });
    setByPeriod(next);
    setLoading(false);
    setCached(signKey, next);
  }, [apiDisabled]);

  useEffect(() => {
    if (selectedSign) fetchAllForSign(selectedSign);
  }, [selectedSign, fetchAllForSign]);

  const getMeta = (data) => {
    if (!data) return null;
    if (data.date) return data.date;
    if (data.week) return `Viikko ${data.week}`;
    if (data.month && data.year) return `${data.month} ${data.year}`;
    if (data.year) return `${data.year}`;
    return null;
  };

  return (
    <div className="horoscope-page">
      <div className="horoscope-page-container">
        <header className="horoscope-page-header">
          <h1>Horoskoopit</h1>
          <p className="horoscope-page-lead">
            Valitse merkki — näet samalla sivulla päivä-, viikko-, kuukausi- ja vuosihoroskoopin.
          </p>
        </header>

        <section className="horoscope-signs" aria-label="Valitse merkki">
          <div className="horoscope-signs-grid">
            {zodiacSigns.map((sign) => (
              <button
                key={sign.name}
                type="button"
                className={`horoscope-sign-btn ${selectedSign?.name === sign.name ? 'is-active' : ''}`}
                onClick={() => setSelectedSign(sign)}
              >
                <span className="horoscope-sign-btn-symbol">{sign.symbol}</span>
                <span className="horoscope-sign-btn-name">{sign.nameFi}</span>
                <span className="horoscope-sign-btn-dates">{sign.dates}</span>
              </button>
            ))}
          </div>
        </section>

        {loading && (
          <div className="horoscope-page-loading">
            <div className="horoscope-page-spinner" aria-hidden />
            <p>Ladataan horoskooppeja…</p>
          </div>
        )}

        {selectedSign && !loading && (
          <section className="horoscope-results" aria-labelledby="horoscope-results-heading">
            <h2 id="horoscope-results-heading" className="horoscope-results-title">
              <span className="horoscope-results-sign">{selectedSign.symbol} {selectedSign.nameFi}</span>
            </h2>

            <div className="horoscope-periods">
              {PERIODS.map((p) => {
                const data = byPeriod[p.id];
                const meta = getMeta(data);
                return (
                  <article
                    key={p.id}
                    className="horoscope-period-card"
                    aria-labelledby={`period-${p.id}`}
                  >
                    <h3 id={`period-${p.id}`} className="horoscope-period-label">
                      {p.label}
                      {meta && <span className="horoscope-period-meta"> — {meta}</span>}
                    </h3>
                    {data?.horoscope ? (
                      <>
                        <p className="horoscope-period-text">{horoscopeTextWithFinnishSign(data.horoscope, selectedSign.name)}</p>
                        {(data.element || data.rulingPlanet) && (
                          <footer className="horoscope-period-footer">
                            {data.element && <span>Elementti: {ELEMENT_FI[data.element] || data.element}</span>}
                            {data.rulingPlanet && (
                              <span className="horoscope-period-planet">
                                <FiStar aria-hidden /> {PLANET_FI[data.rulingPlanet] || data.rulingPlanet}
                              </span>
                            )}
                          </footer>
                        )}
                      </>
                    ) : (
                      <p className="horoscope-period-text horoscope-period-empty">
                        Sisältöä ei juuri nyt saatavilla.
                      </p>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {!selectedSign && (
          <p className="horoscope-page-hint">
            Valitse yllä olevista merkeistä nähdäksesi horoskoopit.
          </p>
        )}
      </div>
    </div>
  );
};

export default Horoscope;
